import { useEffect, useState } from "react";
// Merges machine-translated content over the English source based on the active locale.
// English is the source of truth; translations fall back to English field-by-field.
import es from "@/data/content_translations/es.json";
import fr from "@/data/content_translations/fr.json";
import zh from "@/data/content_translations/zh.json";

const TR = { es, fr, zh };

// Translations produced in Admin (DB) are layered over the bundled ones at runtime.
export function setRuntimeTranslations(lang, guides) {
  const l = normalizeLang(lang);
  TR[l] = { ...(TR[l] || {}), guides: { ...((TR[l] || {}).guides || {}), ...(guides || {}) } };
  if (typeof window !== "undefined") window.dispatchEvent(new Event("fab:translations"));
}

// Re-renders a component when runtime translations arrive after it mounted.
export function useTranslationsTick() {
  const [tick, setTick] = useState(0);
  useEffect(() => { const h = () => setTick((x) => x + 1); window.addEventListener("fab:translations", h); return () => window.removeEventListener("fab:translations", h); }, []);
  return tick;
}

export function normalizeLang(lang) {
  return (lang || "en").split("-")[0];
}

export function localizeModule(mod, lang) {
  const tr = TR[normalizeLang(lang)]?.modules?.[mod.id];
  if (!tr) return { module: mod, machineTranslated: false };
  return {
    machineTranslated: true,
    module: {
      ...mod,
      title: tr.title ?? mod.title,
      summary: tr.summary ?? mod.summary,
      why: tr.why ?? mod.why,
      objectives: tr.objectives ?? mod.objectives,
      sections: (mod.sections || []).map((s, i) => ({
        ...s, heading: tr.sections?.[i]?.heading ?? s.heading, body: tr.sections?.[i]?.body ?? s.body,
      })),
      keyTerms: (mod.keyTerms || []).map((k, i) => ({
        ...k, term: tr.keyTerms?.[i]?.term ?? k.term, def: tr.keyTerms?.[i]?.def ?? k.def,
      })),
      mistakes: tr.mistakes ?? mod.mistakes,
      quiz: (mod.quiz || []).map((q, i) => ({
        ...q, q: tr.quiz?.[i]?.q ?? q.q, options: tr.quiz?.[i]?.options ?? q.options,
      })),
      codeSectionsTitle: tr.codeSectionsTitle ?? mod.codeSectionsTitle,
      groups: mod.groups?.map((g, i) => ({ ...g, title: tr.groups?.[i]?.title ?? g.title })),
      codeSections: mod.codeSections?.map((cs, i) => {
        const t = tr.codeSections?.[i] || {};
        return {
          ...cs, title: t.title ?? cs.title, rule: t.rule ?? cs.rule, example: t.example ?? cs.example, groupTitle: t.groupTitle ?? cs.groupTitle,
          causes: t.causes ?? cs.causes, steps: t.steps ?? cs.steps, lab: cs.lab ? { ...cs.lab, label: t.lab?.label ?? cs.lab.label } : cs.lab,
          ref: cs.ref ? { chapter: t.ref?.chapter ?? cs.ref.chapter, section: t.ref?.section ?? cs.ref.section } : cs.ref,
          checks: cs.checks.map((c, j) => ({ ...c, q: t.checks?.[j]?.q ?? c.q, options: t.checks?.[j]?.options ?? c.options })),
        };
      }),
    },
  };
}

export const hasGuideTranslation = (slug, lang) => !!TR[normalizeLang(lang)]?.guides?.[slug];

export function localizeGuide(g, lang) {
  const tr = TR[normalizeLang(lang)]?.guides?.[g.slug];
  if (!tr) return { guide: g, machineTranslated: false };
  return {
    machineTranslated: true,
    guide: {
      ...g,
      title: tr.title ?? g.title,
      description: tr.description ?? g.description,
      excerpt: tr.excerpt ?? g.excerpt,
      heroAlt: tr.heroAlt ?? g.heroAlt,
      category: tr.category ?? g.category,
      sections: (g.sections || []).map((s, i) => ({
        ...s, h: tr.sections?.[i]?.h ?? s.h, p: tr.sections?.[i]?.p ?? s.p,
      })),
    },
  };
}


// Capstone rubric is machine-translated (English is the source of truth). Returns the merged
// rubric plus a machineTranslated flag that drives the "pending human review" banner. Life-safety
// terms inside the prose are covered by that banner; their glossary translations are held pending review.
export function localizeCapstone(baseRubric, lang) {
  const tr = TR[normalizeLang(lang)]?.capstone?.rubric;
  if (!tr) return { rubric: baseRubric, machineTranslated: false };
  return {
    machineTranslated: true,
    rubric: {
      ...baseRubric,
      deliverables: tr.deliverables ?? baseRubric.deliverables,
      criteria: baseRubric.criteria.map((c, i) => ({
        ...c,
        name: tr.criteria?.[i]?.name ?? c.name,
        levels: tr.criteria?.[i]?.levels ?? c.levels,
      })),
    },
  };
}

// Machine-translated guided/challenge practice activities for a module (answer index preserved).
export function localizeActivities(moduleId, baseActivities, lang) {
  const tr = TR[normalizeLang(lang)]?.activities?.[moduleId];
  if (!tr || !baseActivities) return { activities: baseActivities, machineTranslated: false };
  const mergeAct = (base, t) => (base && t ? { ...base, q: t.q ?? base.q, options: t.options ?? base.options, hint: t.hint ?? base.hint } : base);
  return {
    machineTranslated: true,
    activities: { ...baseActivities, guided: mergeAct(baseActivities.guided, tr.guided), challenge: mergeAct(baseActivities.challenge, tr.challenge) },
  };
}
